import React, { createContext, FC, ReactNode, useEffect, useMemo, useState } from 'react';

import { visitIQSettings } from '~/constants';
import { User } from '~/constants/graphqlTypes';
import { useAuthContext } from '~/contexts/AuthContext';
import {
  FilterDateField,
  GoogleAuthorizeResponse,
  OrganizationType,
  Permission,
  Scope,
  useGoogleAuthorizeMutation,
  useMeQuery,
} from '~/types/api.graphql';
import { getUserOrganizationType } from '~/utils/User';

export type Nullable<T> = T | null;

export interface SessionSettings {
  isOnboadingShown?: boolean;
  journeyGlobalAudience?: boolean;
  journeyPolygons?: boolean;
  isSideBarCollapsed?: boolean;
  isBusinessSubscriptionShown?: boolean;
  isBusinessExportWarningDontShowAgain?: boolean;
  uniquesPixelHitFilter?: FilterDateField;
}

interface Organization {
  id: string;
  parentOrganization?: Organization;
  hiddenPeopleFields?: string[];
}
interface SessionContextProps {
  me: Nullable<User>;
  refetchMeQuery: () => Promise<unknown>;
  settings: Nullable<SessionSettings>;
  updateSettingsProperty: (property: keyof SessionSettings, value: any) => void;
  hasOrgWithPaidSubscription: boolean;
  hasOrgWithSubscriptionOverride: boolean;
  hasOrgWithBusinessDataEnabled: boolean;
  isOrgAdmin: boolean;
  organizationType: OrganizationType;
  orgMenuItemTypes: OrganizationType[];
  hasSingleCustomerOrg: boolean;
  hiddenFieldsIntersection: string[];
  hasAudienceOnlyAccess: boolean;
  hasAccessToAudience: boolean;
  hasInviteToAnOrganization: boolean;
  loading: boolean;
  getHiddenPeopleFieldsForOrg: (org: Organization) => string[] | undefined;
  getHiddenPeopleFieldsForIntegrationContext: (
    org: Organization,
    bypassOrgHiddenFields?: boolean,
  ) => string[] | undefined;
  lastGoogleAccount: Nullable<LastGoogleAccount>;
  setLastGoogleAccount: (info: Nullable<LastGoogleAccount>) => void;
  postGoogleLogin: (code: string, scopes: string[]) => Promise<GoogleAuthorizeResponse>;
  shouldDisconnectGoogleAccount: (err: any) => boolean;
}

interface SessionProviderProps {
  children: ReactNode;
}

const defaultValues: SessionContextProps = {
  me: null,
  refetchMeQuery: null,
  settings: null,
  updateSettingsProperty: null,
  hasOrgWithPaidSubscription: false,
  hasOrgWithSubscriptionOverride: false,
  hasOrgWithBusinessDataEnabled: false,
  isOrgAdmin: false,
  organizationType: OrganizationType.Customer,
  orgMenuItemTypes: [],
  hasSingleCustomerOrg: false,
  hiddenFieldsIntersection: [],
  hasAudienceOnlyAccess: false,
  hasAccessToAudience: false,
  loading: false,
  hasInviteToAnOrganization: false,
  getHiddenPeopleFieldsForOrg: null,
  getHiddenPeopleFieldsForIntegrationContext: null,
  lastGoogleAccount: null,
  setLastGoogleAccount: null,
  postGoogleLogin: null,
  shouldDisconnectGoogleAccount: null,
};

const SessionContext = createContext<SessionContextProps>(defaultValues);
type LastGoogleAccount = { accountId: string; scopes: string[]; expiryDate: string };

export const SessionProvider: FC<SessionProviderProps> = ({ children }) => {
  const { authToken } = useAuthContext();

  const {
    data,
    refetch: refetchMeQuery,
    loading,
  } = useMeQuery({
    skip: !authToken,
    fetchPolicy: 'cache-and-network',
  });

  const [settings, updateSettings] = useState<SessionSettings>(undefined);

  const [lastGoogleAccount, setLastGoogleAccount] = useState<Nullable<LastGoogleAccount>>(null);

  const hasOrgWithPaidSubscription = useMemo(
    () => data?.me?.organizations?.some((org) => org?.paidSubscription),
    [data],
  );
  const hasOrgWithSubscriptionOverride = useMemo(
    () => data?.me?.organizations?.some((org) => org?.subscriptionOverride),
    [data],
  );
  const hasOrgWithBusinessDataEnabled = useMemo(
    () => data?.me?.organizations?.some((org) => org?.businessDataEnabled),
    [data],
  );

  const isOrgAdmin = useMemo(() => data?.me?.scopes?.includes(Scope.OrgsAdmin) || false, [data]);
  const isAudienceAdmin = useMemo(() => data?.me?.scopes?.includes(Scope.AudiencesAdmin) || false, [data]);

  const hasAudienceOnlyAccess = useMemo(() => {
    // If the user does not have any organization, they will have at least
    // one audience access
    const canReadOrganization = (p: Permission) => [Permission.ReadOrganization, Permission.SuperOwner].includes(p);
    // Only check organizations length when data is loaded
    return (
      loading ||
      (!isOrgAdmin &&
        !data?.me?.userRoles?.some(
          (role) =>
            role.assignedRole.permissions.some(canReadOrganization) ||
            role.assignedRole.childOrgExtraPermissions?.some(canReadOrganization),
        ))
    );
  }, [data?.me?.userRoles, loading, isOrgAdmin]);

  const hasAccessToAudience = useMemo(() => {
    const canReadAudience = (p: Permission) => [Permission.ReadAudience, Permission.SuperOwner].includes(p);
    return (
      loading ||
      isAudienceAdmin ||
      data?.me?.userRoles?.some(
        (role) =>
          role.assignedRole.permissions.some(canReadAudience) ||
          role.assignedRole.childOrgExtraPermissions?.some(canReadAudience),
      )
    );
  }, [data?.me?.userRoles, isAudienceAdmin, loading]);

  const hasInviteToAnOrganization = useMemo(() => data?.me?.invites?.length > 0, [data?.me?.invites]);

  const hiddenFieldsIntersection = useMemo(() => {
    const userRoles = data?.me?.userRoles;

    if (!userRoles || userRoles.length === 0) {
      return [];
    }

    // Filter out roles without hiddenPeopleFields
    const rolesWithHiddenFields = userRoles.filter((role) => !!role.hiddenPeopleFields?.length);

    // If any role doesn't have hiddenPeopleFields, return empty array
    if (rolesWithHiddenFields.length !== userRoles.length) {
      return [];
    }

    // Calculate intersection
    return rolesWithHiddenFields.reduce((intersection, role, index) => {
      if (index === 0) {
        return [...(role.hiddenPeopleFields || [])];
      }
      return intersection.filter((field) => role.hiddenPeopleFields?.includes(field));
    }, [] as string[]);
  }, [data]);

  const getParent = (org: Organization) => org?.parentOrganization;

  const getHiddenPeopleFieldsForOrg = (org: Organization) => {
    // Collect all possible orgs to check (org, parent, parent's parent)
    const orgsToCheck = [org, getParent(org), getParent(getParent(org))].filter(Boolean);
    // Get the closest UserRole to the param org
    const userRole = orgsToCheck
      .map(({ id: orgId }) => data?.me.userRoles.find((role) => role.organizationId === orgId))
      .find(Boolean);

    // The BE returns the role's hiddenPeopleFields, and falls back to the ones from the org where the user belongs to
    // (and to its parent if also needing to fall back). This handles the use case where an agency's user with wider
    // visibility can see more fields even when checking more restricted customer orgs' audiences.
    return userRole?.hiddenPeopleFields || [];
  };

  const getHiddenPeopleFieldsForIntegrationContext = (org: Organization, bypassOrgHiddenFields = false) => {
    const orgsToCheck = [org, getParent(org), getParent(getParent(org))].filter(Boolean);
    const mergedHiddenFields = new Set<string>();
    if (!bypassOrgHiddenFields) {
      // Add hidden fields from the orgs
      orgsToCheck.forEach((o) => {
        if (o?.hiddenPeopleFields) {
          o.hiddenPeopleFields.forEach((field) => mergedHiddenFields.add(field));
        }
      });
    }

    // Add hidden fields from user roles
    const userRole = orgsToCheck
      .map(({ id: orgId }) => data?.me.userRoles.find((role) => role.organizationId === orgId))
      .find(Boolean);

    // The userRole.hiddenPeopleFields falls back to the ones from the org where the user belongs to (and to its parent
    // if also needing to fall back). This handles the use case where an agency's user with wider visibility can see
    // more fields even when checking more restricted customer orgs' audiences.
    // On the other hand, the userRole.assignedRole.hiddenPeopleFields only includes the hidden fields of the role.
    const roleHiddenFields = bypassOrgHiddenFields
      ? userRole?.assignedRole?.hiddenPeopleFields
      : userRole?.hiddenPeopleFields;

    roleHiddenFields?.forEach((field) => mergedHiddenFields.add(field));

    return Array.from(mergedHiddenFields);
  };

  const organizationType: OrganizationType = useMemo(() => getUserOrganizationType(data?.me), [data]);

  const hasSingleCustomerOrg = useMemo(
    () => organizationType === OrganizationType.Customer && data?.me?.organizations?.length === 1,
    [organizationType, data],
  );

  const orgMenuItemTypes = useMemo(() => {
    if (organizationType === OrganizationType.Customer) return [];

    let types = [OrganizationType.WhiteLabel, OrganizationType.Agency, OrganizationType.Customer];
    if (organizationType !== OrganizationType.WhiteLabel) {
      types = types.filter((type) => type !== OrganizationType.WhiteLabel);
    }
    return types;
  }, [organizationType]);

  const [authorize] = useGoogleAuthorizeMutation();
  const postGoogleLogin = async (code: string, scopes: string[]): Promise<GoogleAuthorizeResponse> => {
    try {
      const { data } = await authorize({
        variables: { input: { authorizationCode: code, neededScopes: scopes } },
      });

      const response = data.googleAuthorize;

      updateLastGoogleAccount({
        accountId: response.accountId,
        scopes: response.scopes,
        expiryDate: response.expiryDate,
      });

      return response;
    } catch (err: any) {
      if (shouldDisconnectGoogleAccount(err)) {
        setLastGoogleAccount(null);
      }
      throw err;
    }
  };

  useEffect(() => {
    if (data?.me) {
      const settingsItem = localStorage.getItem(`${visitIQSettings}-${data.me.id}`);
      updateSettings(settingsItem ? (JSON.parse(settingsItem) as SessionSettings) : {});
    }
  }, [data?.me]);

  useEffect(() => {
    if (settings && data?.me) {
      if (!settings.uniquesPixelHitFilter) {
        settings.uniquesPixelHitFilter = FilterDateField.PixelFirstHitDate;
      }
      localStorage.setItem(`${visitIQSettings}-${data.me.id}`, JSON.stringify(settings));
    }
  }, [settings, data?.me]);

  useEffect(() => {
    if (data?.me) {
      const lastAccountItem = localStorage.getItem(`visitIQ-lastGoogleAccount-${data.me.id}`);
      setLastGoogleAccount(lastAccountItem ? (JSON.parse(lastAccountItem) as Nullable<LastGoogleAccount>) : null);
    }
  }, [data?.me]);

  const updateLastGoogleAccount = (info: Nullable<LastGoogleAccount>) => {
    setLastGoogleAccount(info);
    if (data?.me) {
      localStorage.setItem(`visitIQ-lastGoogleAccount-${data.me.id}`, JSON.stringify(info));
    }
  };

  const updateSettingsProperty = <Key extends keyof SessionSettings>(property: Key, value: SessionSettings[Key]) => {
    settings[property] = value;
    updateSettings(settings);
    localStorage.setItem(`${visitIQSettings}-${data.me.id}`, JSON.stringify(settings));
  };

  const shouldDisconnectGoogleAccount = (err: any) => {
    const errorMessage = err.graphQLErrors.map((e: any) => e.message).join(' ') as string;
    return (
      errorMessage.includes('Invalid Google credentials') ||
      errorMessage.includes('The Google authorization is missing some required scope(s)')
    );
  };

  return (
    <SessionContext.Provider
      value={{
        me: data?.me || null,
        loading,
        refetchMeQuery,
        settings,
        isOrgAdmin,
        organizationType,
        orgMenuItemTypes,
        hasSingleCustomerOrg,
        updateSettingsProperty,
        hasOrgWithPaidSubscription,
        hasOrgWithSubscriptionOverride,
        hasOrgWithBusinessDataEnabled,
        hiddenFieldsIntersection,
        hasAudienceOnlyAccess,
        hasAccessToAudience,
        hasInviteToAnOrganization,
        getHiddenPeopleFieldsForOrg,
        getHiddenPeopleFieldsForIntegrationContext,
        lastGoogleAccount,
        setLastGoogleAccount: updateLastGoogleAccount,
        postGoogleLogin,
        shouldDisconnectGoogleAccount,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export const useSessionContext: () => SessionContextProps = () => {
  return React.useContext(SessionContext);
};
